import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import { fetchBlogPost } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import CtaBand from "../components/CtaBand";
import { ArrowLeft } from "lucide-react";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    fetchBlogPost(slug)
      .then(setPost)
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <section className="py-32 text-center" data-testid="blog-not-found">
        <h1 className="font-chivo font-black text-3xl text-[#082c50]">{t("blog.not_found")}</h1>
        <Link to="/blog" className="text-[#1a5193] hover:underline mt-4 inline-block">
          {t("blog.back_to_blog")}
        </Link>
      </section>
    );
  }

  if (!post) {
    return <div className="py-32 text-center text-gray-400">Loading...</div>;
  }

  const articleLD = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image,
    author: { "@type": "Organization", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "5999Cargo Group Europe",
      logo: {
        "@type": "ImageObject",
        url: "https://customer-assets.emergentagent.com/job_cargo-to-curacao/artifacts/fp0bzc3n_Logo-5999Cargo.png",
      },
    },
    datePublished: post.published_at,
    keywords: post.tags?.join(", "),
  };

  return (
    <>
      <SEO
        title={`${post.title} | 5999Cargo Blog`}
        description={post.excerpt}
        keywords={post.tags.join(", ")}
        image={post.cover_image}
      />
      <StructuredData id="article-ld" data={articleLD} />

      <article data-testid="blog-post" className="bg-white">
        <div className="relative h-[40vh] min-h-[300px] bg-[#082c50] text-white">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-chivo font-bold text-gray-300 hover:text-white"
                data-testid="blog-back-link"
              >
                <ArrowLeft className="w-3 h-3" /> {t("blog.all_articles")}
              </Link>
              <h1 className="mt-4 font-chivo font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-tight max-w-3xl">
                {post.title}
              </h1>
              <div className="mt-4 text-sm text-gray-300">
                {post.author} · {new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="prose prose-lg max-w-none">
            {post.content.split("\n\n").map((para, i) => {
              if (para.startsWith("## ")) {
                return (
                  <h2 key={i} className="font-chivo font-bold text-2xl text-[#082c50] mt-10 mb-4">
                    {para.replace("## ", "")}
                  </h2>
                );
              }
              return (
                <p key={i} className="text-gray-700 leading-relaxed mb-5 whitespace-pre-line">
                  {para}
                </p>
              );
            })}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span
                key={t}
                className="text-[10px] tracking-[0.2em] uppercase font-chivo font-bold text-[#1a5193] border border-[#1a5193]/30 px-2 py-1"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </article>

      <CtaBand
        title={t("blog.cta_title")}
        subtitle={t("blog.cta_sub")}
      />
    </>
  );
}
